import cn from 'classnames'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'

import { useDeleteEspActivityMutation, useGetEspPartnerQuery } from '../../api/espApi'
import { ESP_TRANSLATION_KEYS } from '../../configs/translations'
import { useEspSponsorshipStatus } from '../../model/hooks/useEspSponsorshipStatus'
import { IEspActivity } from '../../model/types'
import { EspActivityForm } from '../espActivityForm/EspActivityForm'
import { useConfirmation } from '../hooks/useConfirmation'

import { EspActivityTimeline } from './components/espActivityTimeline/EspActivityTimeline'

import styles from './EspActivitiesBoard.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.activities

const ADD_MODE = 'new'

/** Closed (`null`), adding (`ADD_MODE`), or editing the activity with that id. */
type TEspFormTarget = typeof ADD_MODE | string | null

interface IEspActivitiesBoardProps {
  partnerId: string
  /** Opens the sponsor-change request form; omit to hide the banner action. */
  onChangeSponsor?: () => void
  /** Opens the panel at once: `new` to log an engagement, or the id of the one to edit. */
  initialFormTarget?: string
}

/**
 * Account activities tracking (wireframe screen 4): the sponsorship banner on top and the
 * activity log timeline below. The "Log activity" panel is hidden until the banner button
 * (add mode) or a timeline entry's Edit action (edit mode) opens it alongside the list —
 * the list and the form stay on one screen, no view switch and no stacked modal.
 */
export const EspActivitiesBoard: FC<IEspActivitiesBoardProps> = ({
  partnerId,
  onChangeSponsor,
  initialFormTarget,
}) => {
  const { data: espPartner, isLoading } = useGetEspPartnerQuery({ partnerId })
  const activities = espPartner?.activities
  const { sponsorship } = useEspSponsorshipStatus(partnerId)
  const [deleteActivity] = useDeleteEspActivityMutation()
  const deleteConfirmation = useConfirmation()

  const [formTarget, setFormTarget] = useState<TEspFormTarget>(initialFormTarget ?? null)

  const items = useMemo(() => activities ?? [], [activities])

  // Derived, so deleting the edited entry closes the panel instead of leaving it on a
  // stale record.
  const editingActivity = useMemo(
    () =>
      formTarget && formTarget !== ADD_MODE
        ? (items.find((activity) => activity.id === formTarget) ?? null)
        : null,
    [formTarget, items]
  )

  const isFormOpen = formTarget === ADD_MODE || Boolean(editingActivity)

  const handleDelete = useCallback(
    async (activity: IEspActivity) => {
      if (await deleteConfirmation.confirm()) {
        deleteActivity({ activityId: activity.id })
      }
    },
    [deleteActivity, deleteConfirmation]
  )

  const openAddForm = useCallback(() => setFormTarget(ADD_MODE), [])
  const closeForm = useCallback(() => setFormTarget(null), [])

  const hasAutoOpenedRef = useRef(false)

  /**
   * With an empty log there is nothing to read, so the panel opens itself — the board is
   * remounted every time the modal opens, so this runs once per opening. Guarded by a ref
   * rather than the emptiness alone, otherwise closing the form on an empty log would
   * immediately reopen it.
   */
  useEffect(() => {
    if (isLoading || hasAutoOpenedRef.current) {
      return
    }

    hasAutoOpenedRef.current = true

    if (!items.length) {
      setFormTarget(ADD_MODE)
    }
  }, [isLoading, items.length])

  const sponsor = sponsorship?.currentSponsor ?? null

  const assignedAt = useMemo(
    () => sponsorship?.history.find((assignment) => !assignment.unassignedAt)?.assignedAt ?? null,
    [sponsorship]
  )

  const sinceReplaceParams = useMemo(
    () => [{ key: '{date}', value: assignedAt ? new Date(assignedAt).toLocaleDateString() : '—' }],
    [assignedAt]
  )

  const countReplaceParams = useMemo(
    () => [{ key: '{count}', value: String(items.length) }],
    [items.length]
  )

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading} />

      <ConfirmationDialog
        {...deleteConfirmation.dialogProps}
        title={KEYS.deleteConfirmTitle}
        confirmBtnText={KEYS.deleteConfirmBtn}
        rejectBtnText={KEYS.confirmCancelBtn}
        isApplyNegative
        content={<Translation text={KEYS.deleteConfirmMessage} />}
      />

      <div className={styles.banner}>
        <div className={styles.bannerInfo}>
          <b>
            <Translation text={KEYS.bannerActive} span />
          </b>

          {sponsor ? (
            <span className={styles.sponsor}>
              <Translation text={KEYS.bannerSponsor} span />
              &nbsp;
              <span>{sponsor.name}</span>
              {sponsor.email ? (
                <span className={styles.sponsorTitle}> · {sponsor.email}</span>
              ) : null}
            </span>
          ) : null}

          <span className={styles.sinceChip}>
            <Translation text={KEYS.bannerSince} replaceParams={sinceReplaceParams} span />
          </span>
        </div>

        <div className={styles.bannerActions}>
          {onChangeSponsor ? (
            <Button variant="text" color="grey" onClick={onChangeSponsor}>
              <Translation text={KEYS.bannerChangeSponsor} />
            </Button>
          ) : null}

          <Button variant="contained" color="primary" icon="kbase kbase-add" onClick={openAddForm}>
            <Translation text={KEYS.addActivity} />
          </Button>
        </div>
      </div>

      <div className={cn(styles.columns, { [styles.columnsWithForm]: isFormOpen })}>
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardTitle}>
              <Translation text={KEYS.logTitle} span />
            </span>
            <span className={styles.countChip}>
              <Translation text={KEYS.logCount} replaceParams={countReplaceParams} span />
            </span>
          </div>

          {items.length ? (
            <EspActivityTimeline
              activities={items}
              editingActivityId={editingActivity?.id ?? null}
              onEdit={(activity) => setFormTarget(activity.id)}
              onDelete={handleDelete}
            />
          ) : (
            <div className={styles.empty}>
              <Translation text={KEYS.empty} span />
            </div>
          )}
        </section>

        {isFormOpen ? (
          <section className={cn(styles.card, styles.formPanel)}>
            {/* Remount on mode/record change: useGetFormData only seeds an unseeded key. */}
            <EspActivityForm
              key={editingActivity?.id ?? ADD_MODE}
              partnerId={partnerId}
              activity={editingActivity}
              onSaved={closeForm}
              onCancel={closeForm}
            />
          </section>
        ) : null}
      </div>
    </div>
  )
}

EspActivitiesBoard.displayName = 'EspActivitiesBoard'
